<!-- components/Selectores/AutoSelectorTresCamposGenerico.vue -->
<template>
  <div class="flex flex-col gap-3">
    <!-- Marca -->
    <SelectMasInput
      :data="marcas_para_select"
      placeholderName="Marca"
      :isLoading="loading_marcas"
      :isEnabled="habilitar_marca" 
      :mostrar_logo_marca="false"
      :selectedValue="selected_marca_para_select"
      :usar_store_plp="false"
      :disableAutoScroll="true"
      customClassPadre="!h-[38px] !min-h-[38px]"
      customClassTrigger="!h-full !py-0 text-sm"
      customClassInput="!h-full text-sm"
      @changeValue="on_marca_change"     
    />

    <!-- Año -->
    <SelectMasInput
      :data="anos_para_select"
      placeholderName="Año"
      :isLoading="loading_anos"
      :isEnabled="habilitar_ano"
      :selectedValue="selected_ano_para_select"
      :usar_store_plp="false"
      :disableAutoScroll="true"
      customClassPadre="!h-[38px] !min-h-[38px]"
      customClassTrigger="!h-full !py-0 text-sm"
      customClassInput="!h-full text-sm"
      @changeValue="on_ano_change"
    />

    <!-- Modelo -->
    <SelectMasInput
      :data="modelos_para_select"
      placeholderName="Modelo"
      :isLoading="loading_modelos"
      :isEnabled="habilitar_modelo"
      :selectedValue="selected_modelo_para_select"
      :usar_store_plp="false"
      :disableAutoScroll="true"
      customClassPadre="!h-[38px] !min-h-[38px]"
      customClassTrigger="!h-full !py-0 text-sm"
      customClassInput="!h-full text-sm"
      @changeValue="on_modelo_change"
    />
  </div>
</template>

<script setup>
import SelectMasInput from "~/components/Selectores/SelectMasInput.vue";
import { useStore } from "vuex";
import { NEGOCIOS } from "~/constants/negocios";

const store = useStore();
const emit = defineEmits(["carSelected"]);
const route = useRoute();
const config = useRuntimeConfig();

const es_dev = computed(() => (config?.public?.modo || "").toUpperCase() === "DEV");

const props = defineProps({
  // override desde el padre
  modo_sistema: { type: String, required: false, default: "" },

  // override de namespace (si un módulo no coincide con el modo)
  store_ns: { type: String, required: false, default: "" },

  // override de getter (agnóstico)
  store_map: {
    type: Object,
    required: false,
    default: () => ({
      getters: {
        searchData: "getSearchData",
      },
    }),
  },
});

const modo_sistema_state = useState("modo_sistema");

/* =========================
 * helpers
 * ========================= */
const limpiar = (v) => (v ?? "").toString().trim();

const normalizar_modo = (v) =>
  limpiar(v)
    .toLowerCase()
    .replace(/[\s\-_]+/g, "")
    .replace(/[^a-z0-9]/g, "");

const modo_desde_ruta = () => {
  const seg = (limpiar(route.path).split("/").filter(Boolean)[0] || "").trim();
  return seg;
};

/* =========================
 * modo_sistema efectivo: prop > state > ruta
 * ========================= */
const modo_sistema_efectivo = computed(() => {
  const entrada = limpiar(props.modo_sistema);
  if (entrada) return entrada;

  const st = limpiar(modo_sistema_state.value);
  if (st) return st;

  return modo_desde_ruta();
});

const modo_key = computed(() => normalizar_modo(modo_sistema_efectivo.value) || "frenos");

/* =========================
 * factory: modo -> ns + composable
 * ========================= */
const FACTORY_AUTOS = {
  [NEGOCIOS.ACEITES]: { ns: "aceites", composable: () => (typeof useAceites === "function" ? useAceites() : null) },

  [NEGOCIOS.FRENOS]: { ns: "frenos", composable: () => (typeof useFrenos === "function" ? useFrenos() : null) },
  frenos: { ns: "frenos", composable: () => (typeof useFrenos === "function" ? useFrenos() : null) },

  [NEGOCIOS.AFINAMIENTO]: { ns: "afinamiento", composable: () => (typeof useAfinamiento === "function" ? useAfinamiento() : null) },

  [NEGOCIOS.TRENDELANTERO]: { ns: "trendelantero", composable: () => (typeof useTrenDelantero === "function" ? useTrenDelantero() : null) },

  [NEGOCIOS.KILOMETRAJE]: { ns: "kilometraje", composable: () => (typeof useKilometraje === "function" ? useKilometraje() : null) },

  [NEGOCIOS.AMORTIGUADORES]: { ns: "amortiguadores", composable: () => (typeof useAmortiguadores === "function" ? useAmortiguadores() : null) },
  amortiguadores: { ns: "amortiguadores", composable: () => (typeof useAmortiguadores === "function" ? useAmortiguadores() : null) },

  [NEGOCIOS.BALANCEO]: { ns: "balanceo", composable: () => (typeof useBalanceo === "function" ? useBalanceo() : null) },
  balanceo: { ns: "balanceo", composable: () => (typeof useBalanceo === "function" ? useBalanceo() : null) },

  [NEGOCIOS.GEOMETRIA]: { ns: "geometria", composable: () => (typeof useGeometria === "function" ? useGeometria() : null) },

  [NEGOCIOS.HIGIENIZACION]: { ns: "higienizacion", composable: () => (typeof useHigienizacion === "function" ? useHigienizacion() : null) },
  higienizacion: { ns: "higienizacion", composable: () => (typeof useHigienizacion === "function" ? useHigienizacion() : null) },

  [NEGOCIOS.FLUIDOS]: { ns: "fluidos", composable: () => (typeof useFluidos === "function" ? useFluidos() : null) },
  fluidos: { ns: "fluidos", composable: () => (typeof useFluidos === "function" ? useFluidos() : null) },
  
  [NEGOCIOS.REVISIONTECNICA]: { ns: 'revisiontecnica', composable: () => (typeof useRevisionTecnica === 'function' ? useRevisionTecnica() : null) },
};

const definicion_autos = computed(() => {
  const raw = limpiar(modo_sistema_efectivo.value);
  const norm = modo_key.value;

  return (
    FACTORY_AUTOS[raw] ||
    FACTORY_AUTOS[norm] ||
    FACTORY_AUTOS[NEGOCIOS.FRENOS] ||
    FACTORY_AUTOS.frenos
  );
});

/* =========================
 * namespace efectivo: prop store_ns (si existe y está montado) > factory
 * ========================= */
const namespace = computed(() => {
  const ns_prop = limpiar(props.store_ns);
  if (ns_prop && store.hasModule?.(ns_prop)) return ns_prop;
  return definicion_autos.value.ns;
});

/* =========================
 * cache composable API
 * ========================= */
const cache_api_autos = new Map();
const api_autos = shallowRef(null);

const cargar_api_autos = () => {
  const key = modo_key.value || "frenos";

  if (cache_api_autos.has(key)) {
    api_autos.value = cache_api_autos.get(key);
    return;
  }

  const instancia = definicion_autos.value?.composable?.() ?? null;
  cache_api_autos.set(key, instancia);
  api_autos.value = instancia;
};

const obtener_api_autos = (metodo) => {
  const api = api_autos.value;

  if (!api || typeof api[metodo] !== "function") {
    if (es_dev.value) {
      console.error("[AutoSelectorTresCampos] API autos inválida", {
        modo_sistema_efectivo: modo_sistema_efectivo.value,
        modo_key: modo_key.value,
        namespace: namespace.value,
        metodo,
        api_keys: api ? Object.keys(api) : [],
      });
    }
    return null;
  }
  return api;
};

/* =========================
 * wrappers API
 * ========================= */
const buscar_marcas = async () => {
  const api = obtener_api_autos("buscarMarcas");
  return api ? await api.buscarMarcas() : [];
};

const buscar_anos_por_marca = async (marca) => {
  const api = obtener_api_autos("buscarAnosPorMarca");
  return api ? await api.buscarAnosPorMarca(marca) : [];
};

const buscar_modelos_por_marca_ano = async (marca, ano) => {
  const api = obtener_api_autos("buscarModelosPorMarcaAno");
  return api ? await api.buscarModelosPorMarcaAno(marca, ano) : [];
};

/* =========================
 * store read agnóstico
 * ========================= */
const get_search_data_seguro = () => {
  const getter_name = props.store_map?.getters?.searchData || "getSearchData";
  const key = `${namespace.value}/${getter_name}`;
  return store.getters?.[key] ?? { marca: "", ano: "", modelo: "" };
};

const searchData = computed(() => get_search_data_seguro());

const selected_marca = computed(() => searchData.value.marca ?? "");
const selected_ano = computed(() => searchData.value.ano ?? "");
const selected_modelo = computed(() => searchData.value.modelo ?? "");

/* =========================
 * listas + loading
 * ========================= */
const marcas = ref([]);
const anos = ref([]);
const modelos = ref([]);

const loading_marcas = ref(false);
const loading_anos = ref(false);
const loading_modelos = ref(false);

const reset_listas = () => {
  marcas.value = [];
  anos.value = [];
  modelos.value = [];
};

/* =========================
 * normalización para SelectMasInput
 * -> [{ id, name, logo? }]
 * ========================= */
const a_item_select = (item, { con_logo = false } = {}) => {
  if (item === null || item === undefined) {
    return { id: "", name: "" };
  }

  const tipo = typeof item;

  // primitives
  if (tipo === "string" || tipo === "number") {
    return {
      id: item,
      name: String(item),
      ...(con_logo ? { logo: "" } : {}),
    };
  }

  // objects
  const id =
    item?.id ??
    item?.value ??
    item?.codigo ??
    item?.key ??
    item?.anio ??
    item?.ano ??
    item?.year ??
    item?.nombre ??
    item?.name ??
    "";

  const name =
    item?.name ??
    item?.nombre ??
    item?.label ??
    item?.modelo ??
    String(id ?? "");

  const logo =
    item?.logo ??
    item?.marca_logo ??
    item?.image ??
    item?.imagen ??
    item?.icon ??
    "";

  return {
    id,
    name: String(name),
    ...(con_logo ? { logo: logo || "" } : {}),
  };
};

const normalizar_para_select = (lista, { con_logo = false } = {}) => {
  if (!Array.isArray(lista) || lista.length === 0) return [];
  return lista
    .map((x) => a_item_select(x, { con_logo }))
    .filter((x) => x && x.id !== "" && x.name !== "");
};

const marcas_para_select = computed(() => normalizar_para_select(marcas.value, { con_logo: true }));
const anos_para_select = computed(() => normalizar_para_select(anos.value));
const modelos_para_select = computed(() => normalizar_para_select(modelos.value));

/* =========================
 * FIX CLAVE: coacción de tipo para selectedValue
 * Evita el bug: "123" vs 123 (string vs number)
 * ========================= */
const coaccionar_selected = (valor, opciones) => {
  const v = valor;

  if (v === null || v === undefined) return "";
  if (!Array.isArray(opciones) || opciones.length === 0) return v;

  // Si ya existe match exacto, no tocamos
  if (opciones.some((o) => o?.id === v)) return v;

  // string -> number si corresponde
  if (typeof v === "string") {
    const t = v.trim();
    if (!t) return "";

    // si hay match exacto con string id
    if (opciones.some((o) => o?.id === t)) return t;

    // si parece número y hay ids numéricos
    const asNum = Number(t);
    if (!Number.isNaN(asNum) && opciones.some((o) => o?.id === asNum)) return asNum;

    return t;
  }

  // number -> string si los ids están como string
  if (typeof v === "number") {
    const asStr = String(v);
    if (opciones.some((o) => o?.id === asStr)) return asStr;
  }

  return v;
};

const selected_marca_para_select = computed(() => coaccionar_selected(selected_marca.value, marcas_para_select.value));
const selected_ano_para_select = computed(() => coaccionar_selected(selected_ano.value, anos_para_select.value));
const selected_modelo_para_select = computed(() => coaccionar_selected(selected_modelo.value, modelos_para_select.value));

/* =========================
 * habilitaciones equivalentes a SelectCustomV2
 * (y a tus condiciones previas)
 * ========================= */
const habilitar_marca = computed(() => {
  if (loading_marcas.value) return false;
  return marcas_para_select.value.length >= 2;
});

const habilitar_ano = computed(() => {
  if (loading_anos.value) return false;
  if (!selected_marca.value) return false;
  return anos_para_select.value.length >= 2;
});

const habilitar_modelo = computed(() => {
  if (loading_modelos.value) return false;
  if (!selected_ano.value) return false;
  return modelos_para_select.value.length >= 2;
});

/* =========================
 * anti-stale: request-id por carga
 * ========================= */
let req_marcas = 0;
let req_anos = 0;
let req_modelos = 0;

const load_marcas = async () => {
  const id = ++req_marcas;
  if (loading_marcas.value) return;

  try {
    loading_marcas.value = true;
    const lista = await buscar_marcas();

    if (id !== req_marcas) return;
    marcas.value = Array.isArray(lista) ? lista : [];
  } catch (e) {
    if (es_dev.value) console.error("[AutoSelectorTresCampos] load_marcas fallo:", e);
    if (id !== req_marcas) return;
    marcas.value = [];
  } finally {
    if (id === req_marcas) loading_marcas.value = false;
  }
};

const load_anos = async (marca) => {
  const id = ++req_anos;
  if (loading_anos.value) return;

  try {
    loading_anos.value = true;
    const lista = await buscar_anos_por_marca(marca);

    if (id !== req_anos) return;
    anos.value = Array.isArray(lista) ? lista : [];

    if (anos.value.length === 1) {
      const unico_ano = anos.value[0];
      emit("carSelected", { marca, ano: unico_ano, modelo: "" });
      await load_modelos(marca, unico_ano);
    }
  } catch (e) {
    if (es_dev.value) console.error("[AutoSelectorTresCampos] load_anos fallo:", e);
    if (id !== req_anos) return;
    anos.value = [];
  } finally {
    if (id === req_anos) loading_anos.value = false;
  }
};

const load_modelos = async (marca, ano) => {
  const id = ++req_modelos;
  if (loading_modelos.value) return;

  try {
    loading_modelos.value = true;
    const lista = await buscar_modelos_por_marca_ano(marca, ano);

    if (id !== req_modelos) return;
    modelos.value = Array.isArray(lista) ? lista : [];

    if (modelos.value.length === 1) {
      const unico_modelo = modelos.value[0];
      emit("carSelected", { marca, ano, modelo: unico_modelo });
    }
  } catch (e) {
    if (es_dev.value) console.error("[AutoSelectorTresCampos] load_modelos fallo:", e);
    if (id !== req_modelos) return;
    modelos.value = [];
  } finally {
    if (id === req_modelos) loading_modelos.value = false;
  }
};

const inicializar = async () => {
  await load_marcas();

  // Si ya venía seleccionado desde store/localStorage
  if (selected_marca.value) {
    await load_anos(selected_marca.value);

    if (selected_ano.value) {
      await load_modelos(selected_marca.value, selected_ano.value);
    }
  }
};

/* =========================
 * handlers UI
 * ========================= */
const on_marca_change = async (marca) => {
  anos.value = [];
  modelos.value = [];

  if (marca) {
    await load_anos(marca);
  }

  if (anos.value.length !== 1) {
    emit("carSelected", { marca, ano: "", modelo: "" });
  }
};

const on_ano_change = async (ano) => {
  modelos.value = [];

  if (ano && selected_marca.value) {
    await load_modelos(selected_marca.value, ano);
  }

  if (modelos.value.length !== 1) {
    emit("carSelected", { marca: selected_marca.value, ano, modelo: "" });
  }
};

const on_modelo_change = async (modelo) => {
  emit("carSelected", {
    marca: selected_marca.value,
    ano: selected_ano.value,
    modelo,
  });
};

/* =========================
 * lifecycle
 * ========================= */
onMounted(async () => {
  cargar_api_autos();
  await inicializar();
});

watch(
  () => modo_key.value,
  async () => {
    if (!import.meta.client) return;

    // invalida requests en curso y limpia UI
    req_marcas++;
    req_anos++;
    req_modelos++;

    emit("carSelected", { marca: "", ano: "", modelo: "" });

    reset_listas();
    cargar_api_autos();
    await inicializar();
  }
);
</script>
